import { type ReactNode } from "react";
import { ProgressCard } from "../common/ProgressCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { BarChart3, BriefcaseBusiness, HelpCircle } from "lucide-react";
import { VoiceButton } from "@/components/workflow/common/VoiceButton";
import { InfoCallout } from "../common/InfoCallout";
import { useWizard } from "@/contexts/WizardContext";
import { useToast } from "@/hooks/use-toast";
import { LockedBanner } from "../common/LockedBanner";
import { CardPanelHeader } from "../common/CardPanelHeader";
import { SectionCard } from "../common/SectionCard";
import { TransitionMeasuresBlock } from "@/components/workflow/common/TransitionMeasuresBlock";
interface DiversityInclusionCardProps {
  onCollapse: () => void;
}

function FieldBlock({
  label,
  hint,
  children,
}: {
  label: ReactNode;
  hint?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-2">
      <Label className="text-sm font-medium leading-5 text-slate-600">{label}</Label>
      {children}
      {hint ? <p className="text-xs leading-4 text-slate-500">{hint}</p> : null}
    </div>
  );
}


export function DiversityInclusionCard({ onCollapse }: DiversityInclusionCardProps) {
  const {
    diversityInclusionData,
    updateDiversityInclusionData,
    diversityInclusionCompletion,
    forceSave,
    saveStatus,
    isLocked,
    isReadOnly,
  } = useWizard();
  const disabled = isLocked || isReadOnly;
  const { toast } = useToast();
  const handleSave = async () => {
    try {
      await forceSave();
      toast({
        title: "Saved successfully",
        description: "Diversity & inclusion data has been saved.",
      });
    } catch (error) {
      toast({
        title: "Save failed",
        description: "Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <CardPanelHeader
        title={"Diversity and Inclusion (C5)"}
        description={"Track workforce diversity and inclusive practices"}
        onClose={onCollapse}
        onSave={() => {
          void handleSave();
        }}
        saveStatus={saveStatus}
        disabled={disabled}
        tag={"Social"}
      />

      {disabled && <LockedBanner />}

      <div className="flex flex-col gap-6">
        <SectionCard
          header={
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <BarChart3 className="h-4 w-4" />
              </div>
              <h3 className="text-base font-semibold leading-6 text-slate-900 sm:text-lg sm:leading-7">
                {"1) Management Representation (if 50+ employees)"}
              </h3>
            </div>
          }
        >
          <div className="flex flex-col gap-4">
            <div className="grid gap-4 md:grid-cols-2">
              <FieldBlock label={"Female managers (count)"}>
                <Input
                  type="number"
                  placeholder={"e.g. 2"}
                  min="0"
                  value={diversityInclusionData.femaleManagers}
                  onChange={(e) => updateDiversityInclusionData({ femaleManagers: e.target.value })}
                  disabled={disabled}
                  className="form-input h-12 rounded-xl px-4 text-base"
                />
              </FieldBlock>

              <FieldBlock label={"Male managers (count)"}>
                <Input
                  type="number"
                  placeholder={"e.g. 3"}
                  min="0"
                  value={diversityInclusionData.maleManagers}
                  onChange={(e) => updateDiversityInclusionData({ maleManagers: e.target.value })}
                  disabled={disabled}
                  className="form-input h-12 rounded-xl px-4 text-base"
                />
              </FieldBlock>
            </div>

            <InfoCallout
              label={"Note:"}
              text={"Only required if your organization has 50+ employees"}
            />
          </div>
        </SectionCard>

        <SectionCard
          header={
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <BriefcaseBusiness className="h-4 w-4" />
              </div>
              <h3 className="text-base font-semibold leading-6 text-slate-900 sm:text-lg sm:leading-7">
                {"2) Alternative Workforce (if 50+ employees)"}
              </h3>
            </div>
          }
        >
          <div className="flex flex-col gap-4">
            <div className="grid gap-4 md:grid-cols-2">
              <FieldBlock
                label={"Self-employed workers (exclusive)"}
                hint={"Not counted as employees"}
              >
                <Input
                  type="number"
                  placeholder={"e.g. 1"}
                  min="0"
                  value={diversityInclusionData.selfEmployedWorkers}
                  onChange={(e) =>
                    updateDiversityInclusionData({ selfEmployedWorkers: e.target.value })
                  }
                  disabled={disabled}
                  className="form-input h-12 rounded-xl px-4 text-base"
                />
              </FieldBlock>

              <FieldBlock
                label={"Temporary agency workers"}
                hint={"Not on payroll"}
              >
                <Input
                  type="number"
                  placeholder={"e.g. 2"}
                  min="0"
                  value={diversityInclusionData.temporaryAgencyWorkers}
                  onChange={(e) =>
                    updateDiversityInclusionData({ temporaryAgencyWorkers: e.target.value })
                  }
                  disabled={disabled}
                  className="form-input h-12 rounded-xl px-4 text-base"
                />
              </FieldBlock>
            </div>

            <InfoCallout
              label={"Note:"}
              text={"Only required if your organization has 50+ employees"}
            />
          </div>
        </SectionCard>

        <SectionCard
          header={
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <BarChart3 className="h-4 w-4" />
              </div>
              <h3 className="text-base font-semibold leading-6 text-slate-900 sm:text-lg sm:leading-7">
                {"3) Diversity & Inclusion Policies (Optional)"}
              </h3>
              <HelpCircle className="h-[15px] w-[15px] text-slate-500" />
            </div>
          }
        >
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <Label className="text-sm font-medium leading-5 text-slate-600">
                {"Describe any diversity & inclusion policies or practices you have in place"}
              </Label>

              <VoiceButton
                onTranscript={(text) => updateDiversityInclusionData({ diversityPolicies: text })}
                disabled={disabled}
                variant="ghost"
                className="h-auto justify-start gap-1.5 px-0 py-0 text-xs font-semibold text-primary hover:bg-transparent hover:text-primary/80"
                recordingClassName="text-destructive hover:text-destructive/80"
                iconClassName="h-3.5 w-3.5"
                recordingIconClassName="h-3.5 w-3.5"
                data-testid="button-diversity-voice"
              />
            </div>

            <Textarea
              placeholder={"e.g. Women in leadership development program, Equal pay audit, Inclusive hiring practices, Mentoring for minorities, Anti-discrimination policy, etc."}
              className="form-input min-h-[176px] rounded-xl px-4 py-3 text-sm leading-5"
              value={diversityInclusionData.diversityPolicies}
              onChange={(e) => updateDiversityInclusionData({ diversityPolicies: e.target.value })}
              data-testid="textarea-diversity-policies"
              disabled={disabled}
            />

            <p className="text-xs leading-4 text-slate-500">
              {"Click to describe your D&I practices using voice"}
            </p>
          </div>
        </SectionCard>

        <TransitionMeasuresBlock topicId="diversity-inclusion" currentTab="social" disabled={disabled} />

        <ProgressCard completion={diversityInclusionCompletion} />
      </div>
    </div>
  );
}
